import { css } from '@emotion/react'
import { useState } from 'react'
import { inject, Observer } from 'mobx-react'
import styled from '@emotion/styled'
import { withRouterProps } from '../../../utils'
import { Loading } from '../../../components'

const ButtonGroup = styled.div`
  display: flex;
  justify-content: center;
  flex-wrap: wrap-reverse;
  margin: 36px 0;
  & > a {
    margin: 1rem;
  }
`

const CancelButton = styled.a`
  display: inline-block;
  border: 1px solid #7a8e96;
  background-color: #ffffff;
  border-radius: 4px;
  padding: 12px 16px;
  text-align: center;
  color: #7a8e96;
  font-family: 'Open Sans';
  cursor: pointer;

  &:hover {
    background-color: #f2f2f2;
  }

  &:active {
    background-color: #d9d9d9;
  }

  & > div {
    pointer-events: none;
  }

  & > div:first-of-type {
    font-size: 18px;
    line-height: 1.25;
  }

  & > div:nth-of-type(2),
  div:nth-of-type(3) {
    font-size: 12px;
  }
`
const PrimaryButton = styled.a`
  display: inline-block;
  background: linear-gradient(180deg, #39a3c6 0%, #1291bb 100%);
  border-radius: 4px;
  padding: 12px 16px;
  text-align: center;
  color: white;
  font-family: 'Open Sans';
  cursor: pointer;

  &:hover {
    background: linear-gradient(to bottom, #038ab7 0%, #02769d 100%);
  }

  &:active {
    background: linear-gradient(to bottom, #02607f, #02607f);
  }

  & > div {
    pointer-events: none;
  }

  & > div:first-of-type {
    font-size: 18px;
    line-height: 1.25;
  }

  & > div:nth-of-type(2),
  div:nth-of-type(3) {
    font-size: 12px;
  }
`
const SecondaryButton = styled.a`
  display: inline-block;
  background-color: #4a606a;
  border-radius: 4px;
  padding: 12px 16px;
  text-align: center;
  color: white;
  font-family: 'Open Sans';
  cursor: pointer;

  &:hover {
    background-color: #40525b;
  }

  &:active {
    background-color: #2b373d;
  }

  & > div {
    pointer-events: none;
  }

  & > div:first-of-type {
    font-size: 18px;
    line-height: 1.25;
  }

  & > div:nth-of-type(2),
  div:nth-of-type(3) {
    font-size: 12px;
  }
`

// TO-DO: Change managed account text to point-in-time advice based on templateId
function ValuePropButtons({ navigate, store, offboarding }) {
  const [state, setState] = useState('idle')

  // Do this check in case they get to this page through the back button when they're already a managedAccount
  if (store.account.managedAccount) {
    handleNext()
  }

  function handleNext() {
    navigate(`/implement/${offboarding.nextRoute('value-proposition')}`)
  }

  async function acceptManagedAccount() {
    try {
      setState('loading')

      await store.updateManagedAccountStatus({
        managedAccountStatus: true,
        caseId: store.acceptedCaseId,
        savingsRateTransactionOptional: store.acceptedContributionDistribution.catchUp
          ? store.acceptedContributionDistribution
          : null,
      })

      offboarding.setAcceptedCaseSubmitted(true)

      handleNext()
    } catch (err) {
      console.error(err)
      setState('error')
    }
  }

  function render() {
    if (state === 'loading') {
      return (
        <ButtonGroup>
          <Loading />
        </ButtonGroup>
      )
    }

    const { features } = store

    return (
      <div>
        {state === 'error' && (
          <div
            css={css`
              color: crimson;
              text-align: center;
            `}>
            Oops! Something went wrong, please try again later
          </div>
        )}
        <ButtonGroup>
          <CancelButton onClick={() => navigate('/overall')}>
            <div>Cancel</div>
            <div>no action | return to</div>
            <div>Overall Strategy</div>
          </CancelButton>

          {features.viewAdviceOnlyEnabled && (
            <SecondaryButton onClick={handleNext}>
              <div>View advice only</div>
              <div>no implementation | no ongoing</div>
              <div>review | no guide</div>
            </SecondaryButton>
          )}

          {features.oneTimeAdviceEnabled && (
            <SecondaryButton onClick={handleNext}>
              <div>Implement one-time advice</div>
              <div>single use option over life of</div>
              <div>account | no ongoing review</div>
            </SecondaryButton>
          )}

          {features.managedAccountAvailable && (
            <PrimaryButton onClick={() => acceptManagedAccount()}>
              <div>Accept managed account</div>
              <div>implement | ongoing</div>
              <div>quarterly review</div>
            </PrimaryButton>
          )}
        </ButtonGroup>
      </div>
    )
  }

  return <Observer>{render}</Observer>
}

export default withRouterProps(inject('store', 'offboarding')(ValuePropButtons))
